import { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Card,
  Col,
  Form,
  InputGroup,
  Row,
  Spinner,
} from "react-bootstrap";
import { FaSearch, FaCheck } from "react-icons/fa";
import {
  listWhatsAppWebChannels,
  listWhatsAppWebGroups,
  syncWhatsAppWebGroups,
} from "../../../../services/api";

/**
 * Wizard de 3 passos pra criar/editar segmento (replica OrbitSender).
 * Passo 1: Selecionar canais
 * Passo 2: Selecionar grupos
 * Passo 3: Nome + revisar e salvar
 */
export default function SegmentWizard({ initial, onCancel, onSubmit, saving }) {
  const [step, setStep] = useState(1);
  const [allChannels, setAllChannels] = useState([]);
  const [selectedChannelIds, setSelectedChannelIds] = useState(
    initial?.channelIds || []
  );

  const [allGroups, setAllGroups] = useState([]); // todos os grupos dos canais selecionados
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [selectedGroupJids, setSelectedGroupJids] = useState(
    initial?.groupJids || []
  );
  const [groupSearch, setGroupSearch] = useState("");
  const [syncing, setSyncing] = useState(false);

  const [name, setName] = useState(initial?.name || "");
  const [description, setDescription] = useState(initial?.description || "");

  // Carrega canais
  useEffect(() => {
    listWhatsAppWebChannels().then((res) =>
      setAllChannels(res?.data?.channels || [])
    );
  }, []);

  // Carrega grupos quando entra no passo 2 ou troca de canais
  useEffect(() => {
    if (step !== 2 || selectedChannelIds.length === 0) return;
    setGroupsLoading(true);
    Promise.all(
      selectedChannelIds.map((cid) =>
        listWhatsAppWebGroups({ channelId: cid, pageSize: 1000 })
      )
    )
      .then((results) => {
        const merged = results.flatMap((r) => r?.data || []);
        setAllGroups(merged);
      })
      .finally(() => setGroupsLoading(false));
  }, [step, selectedChannelIds]);

  const filteredGroups = useMemo(() => {
    const q = groupSearch.trim().toLowerCase();
    if (!q) return allGroups;
    return allGroups.filter((g) => g.name?.toLowerCase().includes(q));
  }, [allGroups, groupSearch]);

  function toggleChannel(id) {
    setSelectedChannelIds((curr) =>
      curr.includes(id) ? curr.filter((x) => x !== id) : [...curr, id]
    );
  }

  function toggleGroup(jid) {
    setSelectedGroupJids((curr) =>
      curr.includes(jid) ? curr.filter((x) => x !== jid) : [...curr, jid]
    );
  }

  function selectAllVisible() {
    const visibleJids = filteredGroups.map((g) => g.whatsapp_jid);
    const newSet = new Set([...selectedGroupJids, ...visibleJids]);
    setSelectedGroupJids([...newSet]);
  }

  function clearAllVisible() {
    const visibleJids = new Set(filteredGroups.map((g) => g.whatsapp_jid));
    setSelectedGroupJids((curr) => curr.filter((j) => !visibleJids.has(j)));
  }

  async function handleSyncCurrent() {
    setSyncing(true);
    try {
      await Promise.all(
        selectedChannelIds.map((cid) => syncWhatsAppWebGroups(cid))
      );
      const results = await Promise.all(
        selectedChannelIds.map((cid) =>
          listWhatsAppWebGroups({ channelId: cid, pageSize: 1000 })
        )
      );
      setAllGroups(results.flatMap((r) => r?.data || []));
    } finally {
      setSyncing(false);
    }
  }

  function handleSubmit() {
    onSubmit({
      name: name.trim(),
      description: description.trim(),
      channel_ids: selectedChannelIds,
      group_jids: selectedGroupJids,
    });
  }

  const canGoStep2 = selectedChannelIds.length > 0;
  const canGoStep3 = selectedGroupJids.length > 0;
  const canSubmit = name.trim().length >= 2 && canGoStep3;

  return (
    <Card>
      <Card.Body>
        {/* Stepper */}
        <div className="d-flex justify-content-around mb-4">
          {[1, 2, 3].map((n) => (
            <div
              key={n}
              className="text-center"
              style={{ opacity: step >= n ? 1 : 0.4 }}
            >
              <Badge
                variant={step >= n ? "primary" : "secondary"}
                style={{
                  fontSize: 20,
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {n}
              </Badge>
              <div className="mt-1">
                {n === 1 && "Selecionar Canais"}
                {n === 2 && "Selecionar Grupos"}
                {n === 3 && "Informações"}
              </div>
            </div>
          ))}
        </div>

        {/* Step 1 — Canais */}
        {step === 1 && (
          <>
            <h5 className="mb-3">Selecionar Canais</h5>
            <p className="text-muted">
              Escolha os canais que farão parte deste segmento.
            </p>
            {allChannels.map((c) => (
              <Card
                key={c.id}
                className={`mb-2 ${
                  selectedChannelIds.includes(c.id) ? "border-primary" : ""
                }`}
                style={{ cursor: "pointer" }}
                onClick={() => toggleChannel(c.id)}
              >
                <Card.Body className="d-flex align-items-center py-2">
                  <Form.Check
                    type="checkbox"
                    checked={selectedChannelIds.includes(c.id)}
                    onChange={() => toggleChannel(c.id)}
                  />
                  <div className="ml-3 flex-grow-1">
                    <strong>{c.name}</strong>
                    <Badge
                      variant={c.status === "active" ? "success" : "secondary"}
                      className="ml-2"
                    >
                      {c.status}
                    </Badge>
                  </div>
                </Card.Body>
              </Card>
            ))}
            <div className="d-flex justify-content-between mt-3">
              <Button variant="secondary" onClick={onCancel}>
                Cancelar
              </Button>
              <Button
                variant="primary"
                disabled={!canGoStep2}
                onClick={() => setStep(2)}
              >
                Próximo
              </Button>
            </div>
          </>
        )}

        {/* Step 2 — Grupos */}
        {step === 2 && (
          <>
            <h5 className="mb-3">Selecionar Grupos</h5>
            <Card className="mb-3 bg-dark text-light">
              <Card.Body className="py-2">
                <small>
                  ⚡ {allGroups.length} grupos disponíveis dos canais
                  selecionados
                </small>
                <Button
                  size="sm"
                  variant="outline-light"
                  className="ml-3"
                  disabled={syncing}
                  onClick={handleSyncCurrent}
                >
                  {syncing ? "Sincronizando..." : "Atualizar lista"}
                </Button>
              </Card.Body>
            </Card>

            <Row className="mb-3">
              <Col md={9}>
                <InputGroup>
                  <InputGroup.Prepend>
                    <InputGroup.Text>
                      <FaSearch />
                    </InputGroup.Text>
                  </InputGroup.Prepend>
                  <Form.Control
                    placeholder="Buscar grupos..."
                    value={groupSearch}
                    onChange={(e) => setGroupSearch(e.target.value)}
                  />
                </InputGroup>
              </Col>
              <Col md={3}>
                {selectedGroupJids.some((j) =>
                  filteredGroups.find((g) => g.whatsapp_jid === j)
                ) ? (
                  <Button
                    variant="outline-secondary"
                    className="w-100"
                    onClick={clearAllVisible}
                  >
                    Desmarcar Visíveis
                  </Button>
                ) : (
                  <Button
                    variant="primary"
                    className="w-100"
                    onClick={selectAllVisible}
                  >
                    Selecionar Todos
                  </Button>
                )}
              </Col>
            </Row>

            <p>
              <Badge variant="primary">{selectedGroupJids.length}</Badge>{" "}
              grupos selecionados de {filteredGroups.length} visíveis
            </p>

            {groupsLoading ? (
              <div className="text-center p-4">
                <Spinner animation="border" />
              </div>
            ) : (
              <Row style={{ maxHeight: 400, overflowY: "auto" }}>
                {filteredGroups.map((g) => {
                  const checked = selectedGroupJids.includes(g.whatsapp_jid);
                  return (
                    <Col key={g.whatsapp_jid} md={6} className="mb-2">
                      <Card
                        className={checked ? "border-primary" : ""}
                        style={{ cursor: "pointer" }}
                        onClick={() => toggleGroup(g.whatsapp_jid)}
                      >
                        <Card.Body className="py-2 d-flex align-items-center">
                          <Form.Check
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleGroup(g.whatsapp_jid)}
                          />
                          <div className="ml-2 flex-grow-1">
                            <strong>{g.name || "(sem nome)"}</strong>
                            <div className="small text-muted">
                              {g.channel?.name}
                            </div>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            )}

            <div className="d-flex justify-content-between mt-3">
              <Button variant="secondary" onClick={() => setStep(1)}>
                Voltar
              </Button>
              <Button
                variant="primary"
                disabled={!canGoStep3}
                onClick={() => setStep(3)}
              >
                Próximo
              </Button>
            </div>
          </>
        )}

        {/* Step 3 — Informações */}
        {step === 3 && (
          <>
            <h5 className="mb-3">Informações do Segmento</h5>
            <Form.Group>
              <Form.Label>Nome do Segmento *</Form.Label>
              <Form.Control
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ex: CaçadoresOfertas Geral"
                required
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Descrição (opcional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </Form.Group>

            <Card className="mt-3 bg-dark text-light">
              <Card.Body>
                <Row className="text-center">
                  <Col>
                    <h3 className="mb-0">{selectedChannelIds.length}</h3>
                    <small>Canais Selecionados</small>
                  </Col>
                  <Col>
                    <h3 className="mb-0">{selectedGroupJids.length}</h3>
                    <small>Grupos Selecionados</small>
                  </Col>
                </Row>
                <p className="text-center mt-2 mb-0">
                  Total de <strong>{selectedGroupJids.length}</strong> grupos de{" "}
                  <strong>{allGroups.length}</strong> disponíveis
                </p>
              </Card.Body>
            </Card>

            <div className="d-flex justify-content-between mt-3">
              <Button variant="secondary" onClick={() => setStep(2)}>
                Voltar
              </Button>
              <Button
                variant="primary"
                disabled={!canSubmit || saving}
                onClick={handleSubmit}
              >
                <FaCheck /> {saving ? "Salvando..." : "Salvar"}
              </Button>
            </div>
          </>
        )}
      </Card.Body>
    </Card>
  );
}
